
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Textfit } from 'react-textfit';

interface DisplayCardProps {
  title: string;
  time?: string;
  description?: string;
  showDivider?: boolean;
  showDescriptions?: boolean;
  additionalContent?: React.ReactNode;
  isHomePage?: boolean;
  className?: string;
  dividerPosition?: 'afterTitle' | 'afterTime' | 'afterDescription';
}

const DisplayCard: React.FC<DisplayCardProps> = ({
  title,
  time,
  description,
  showDivider = true,
  showDescriptions = true,
  additionalContent,
  isHomePage = false,
  className = "",
  dividerPosition = 'afterTime',
}) => {
  // Original card design with layered background effects
  const baseCardClasses = "rounded-3xl bg-white/15 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col relative border-2 border-transparent bg-gradient-to-br from-yellow-400/20 via-amber-500/15 to-yellow-600/20";
  
  // Responsive height classes for different screen sizes
  const responsiveHeightClasses = "min-h-[180px] sm:min-h-[220px] md:min-h-[260px] lg:min-h-[300px] xl:min-h-[340px] 2xl:min-h-[400px]";
  
  // Beautiful gold gradient divider - keeping original styling
  const dividerClasses = "w-3/4 h-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 mb-3 mx-auto rounded-full shadow-sm";

  const renderDivider = () => {
    if (!showDivider) return null;
    return <div className={dividerClasses} />;
  };

  return (
    <Card className={`${baseCardClasses} ${responsiveHeightClasses} ${className}`}>
      {/* Gold gradient overlay - first layer */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-yellow-400/30 via-transparent to-amber-500/30 pointer-events-none" />
      
      {/* White backdrop layer - second layer */}
      <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-br from-white/80 to-white/60 backdrop-blur-sm" />
      
      <CardContent className="relative z-10 p-4 sm:p-6 flex flex-col h-full text-center">
        <div className="flex-1 flex flex-col justify-center items-center w-full h-full space-y-2">
          {/* Title with auto-fit text using Textfit - bigger and bolder */}
          <div className="flex-1 w-full flex items-center justify-center min-h-[60px]">
            <Textfit
              mode="multi"
              min={28}
              max={70}
              style={{ width: '100%', height: '100%' }}
              className="leading-tight font-black text-gray-900 text-center flex items-center justify-center"
            >
              {title}
            </Textfit>
          </div>
          
          {dividerPosition === 'afterTitle' && renderDivider()}
          
          {/* Time with auto-fit text using Textfit - bigger and bolder */}
          {time && (
            <div className="flex-1 w-full flex items-center justify-center min-h-[50px]">
              <Textfit
                mode="multi"
                min={24}
                max={64}
                style={{ width: '100%', height: '100%' }}
                className="leading-tight font-black text-gray-800 text-center flex items-center justify-center"
              >
                {time}
              </Textfit>
            </div>
          )}
          
          {dividerPosition === 'afterTime' && renderDivider()}
          
          {/* Description with auto-fit text using Textfit - bigger and bolder */}
          {showDescriptions && description && (
            <div className="flex-1 w-full flex items-center justify-center min-h-[40px]">
              <div className="relative overflow-hidden w-full h-full">
                <Textfit
                  mode="multi"
                  min={20}
                  max={56}
                  style={{ width: '100%', height: '100%' }}
                  className="leading-tight font-black text-gray-700 text-center flex items-center justify-center"
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: description.replace(/data-lov-id="[^"]*"|data-lov-name="[^"]*"|data-component-path="[^"]*"|data-component-line="[^"]*"|data-component-file="[^"]*"|data-component-name="[^"]*"|data-component-content="[^"]*"/g, '')
                    }}
                  />
                </Textfit>
                {/* Fade effect for very long text */}
                <div className="absolute bottom-0 left-0 w-full h-3 bg-gradient-to-t from-white/95 to-transparent pointer-events-none" />
              </div>
            </div>
          )}
          
          {dividerPosition === 'afterDescription' && renderDivider()}
          
          {additionalContent && (
            <div className="mt-2 flex-shrink-0">
              {additionalContent}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DisplayCard;
